/**
 * BP-026 Session 3 · Platform Administration Console layout.
 *
 * Route: `/platform/*` (Kubernetes ingress will rewrite
 * `admin.ispeedtoclose.com` → `/platform/*` at deploy time).
 *
 * Warm-light executive cockpit — matches the rest of the app (cream
 * backdrop, navy ink, amber accents, Cabinet Grotesk display) with
 * denser info layout and slightly heavier contrast to signal
 * power-user mode.
 *
 * Every route requires at least ONE platform capability; the shell
 * gates access via `GET /api/v3/platform/me` and shows a "no access"
 * screen if roles = [].
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, DollarSign, Cpu, Server, ShieldCheck,
  ScrollText, LifeBuoy, ToggleLeft, Megaphone, UserCog, KeyRound, Loader2,
  Users, MessageSquareCode, Rocket, Search, ArrowRight, ChevronRight,
  LogOut, Command, Sparkles,
} from "lucide-react";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Section-grouped nav model.
const SECTIONS = [
  {
    id: "cockpit",
    label: "Cockpit",
    items: [
      { to: "",              label: "Dashboard",       icon: LayoutDashboard, cap: "analytics:view" },
    ],
  },
  {
    id: "business",
    label: "Business",
    items: [
      { to: "tenants",       label: "Tenants",         icon: Building2,       cap: "tenant:view" },
      { to: "users",         label: "Users",           icon: Users,           cap: "user:view" },
      { to: "revenue",       label: "Revenue",         icon: DollarSign,      cap: "billing:view" },
      { to: "waitlist",      label: "Launch Waitlist", icon: Rocket,          cap: "analytics:view" },
    ],
  },
  {
    id: "systems",
    label: "Systems",
    items: [
      { to: "ai",            label: "AI Admin",        icon: Cpu,              cap: "ai:view" },
      { to: "prompts",       label: "Prompt Library",  icon: MessageSquareCode, cap: "prompts:view" },
      { to: "health",        label: "System Health",   icon: Server,           cap: "system:view" },
      { to: "flags",         label: "Feature Flags",   icon: ToggleLeft,       cap: "flags:view" },
      { to: "support",       label: "Support",         icon: LifeBuoy,         cap: "support:view" },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    items: [
      { to: "audit",         label: "Audit Log",       icon: ScrollText,       cap: "audit:view" },
      { to: "announcements", label: "Announcements",   icon: Megaphone,        cap: "announcements:view" },
      { to: "roles",         label: "Roles",           icon: UserCog,          cap: "roles:view" },
      { to: "security",      label: "My Security",     icon: KeyRound,         cap: null },
    ],
  },
];

const ALL_ITEMS = SECTIONS.flatMap((s) => s.items.map((it) => ({ ...it, section: s.label })));

export default function PlatformShell() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const nav = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    apiGet("/api/v3/platform/me")
      .then(setMe)
      .catch((e) => setErr(e?.message || "Unable to load platform identity"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA] text-[var(--ink-2)]">
        <Loader2 className="w-6 h-6 animate-spin mr-2 text-[var(--primary)]" /> Loading platform…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA] p-6">
        <div className="max-w-md text-center rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-900">
          <div className="text-lg font-bold">Platform unavailable</div>
          <div className="mt-1 text-sm">{err}</div>
        </div>
      </div>
    );
  }
  if (!me?.platform_roles?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA] p-8">
        <div data-testid="platform-no-access"
             className="max-w-md text-center rounded-2xl border border-slate-200 bg-white shadow-sm p-8">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[var(--primary)]/12 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-[var(--primary)]" />
          </div>
          <h2 className="mt-4 font-display text-xl font-black text-[var(--ink)]">No platform access</h2>
          <p className="mt-2 text-sm text-[var(--ink-2)] leading-relaxed">
            Your account is authenticated but does not hold a platform role.
            Platform access is granted independently of workspace roles. Ask an
            existing platform administrator to grant you a role, or claim the
            one-shot bootstrap via <code className="text-[var(--primary)] font-mono">PLATFORM_BOOTSTRAP_EMAIL</code>.
          </p>
          <a href="/v3" className="mt-4 inline-block text-sm text-[var(--ink-2)] hover:text-[var(--ink)] underline">
            ← Back to workspace
          </a>
        </div>
      </div>
    );
  }

  const caps = new Set(me.capabilities || []);
  const visibleSections = SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((it) => it.cap == null || caps.has(it.cap)) }))
    .filter((s) => s.items.length > 0);

  const roleLabel = (me.platform_roles?.[0] || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const path = location.pathname.replace(/^\/platform\/?/, "");
  const currentItem = ALL_ITEMS.find((it) => it.to === path) || ALL_ITEMS.find((it) => it.to === "");

  const handleSignOut = async () => {
    try { await logout(); } finally { nav("/login", { replace: true }); }
  };

  return (
    <div className="min-h-screen bg-[#F7F3EA] text-[var(--ink)] relative">
      {/* Faint warm-parchment texture (very subtle) */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.35]" aria-hidden style={{
        backgroundImage:
          "radial-gradient(circle at 12% 8%, rgba(230,167,62,0.06) 0%, transparent 40%)," +
          "radial-gradient(circle at 92% 88%, rgba(11,19,43,0.04) 0%, transparent 45%)",
      }} />

      <div className="relative flex">
        {/* ── Sidebar */}
        <aside data-testid="platform-sidebar"
               className="w-64 shrink-0 border-r border-slate-200 bg-white/80 backdrop-blur-md flex flex-col min-h-screen">
          <div className="p-5 border-b border-slate-200">
            <Link to="/" data-testid="platform-logo" className="block" aria-label="iSpeedToClose home">
              <img src="/brand/wordmark-color.png" alt="iSpeedToClose"
                   className="h-12 w-auto" width="240" height="48" />
            </Link>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[var(--primary)] bg-[var(--primary)]/10 border border-[var(--primary)]/25 rounded-full px-2.5 py-1">
              <ShieldCheck className="w-3 h-3" /> Platform Console
            </div>
            <div className="mt-1.5 text-[11px] text-[var(--ink-2)]">
              {roleLabel || "Super Admin"}
            </div>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
            {visibleSections.map((s) => (
              <div key={s.id}>
                <div className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {s.label}
                </div>
                <div className="space-y-0.5">
                  {s.items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.to === ""}
                      data-testid={`platform-nav-${n.to || "dashboard"}`}
                      className={({ isActive }) =>
                        "group relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all " +
                        (isActive
                          ? "bg-gradient-to-r from-[var(--primary)]/15 to-transparent text-[var(--ink)] font-bold shadow-[inset_3px_0_0_var(--primary)]"
                          : "text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-slate-50")
                      }
                    >
                      <n.icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{n.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          <div className="border-t border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--primary)] to-orange-600 text-white font-black text-xs flex items-center justify-center shadow-sm">
                {(me.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-bold text-[var(--ink)] truncate">{me.email}</div>
                <div className="text-[10px] text-[var(--ink-2)]">{me.capabilities?.length || 0} capabilities</div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main column */}
        <div className="flex-1 min-w-0">
          <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F7F3EA]/85 border-b border-slate-200">
            <div className="px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm min-w-0">
                <span className="text-[var(--ink-2)]">Platform</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[var(--ink)] font-bold truncate">{currentItem?.label || "Dashboard"}</span>
              </div>

              <div className="flex items-center gap-2">
                <button type="button"
                        onClick={() => setPaletteOpen(true)}
                        data-testid="platform-cmdk-trigger"
                        className="hidden md:inline-flex items-center gap-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-[var(--ink-2)] hover:text-[var(--ink)] text-xs px-3 py-1.5 shadow-sm transition-colors">
                  <Search className="w-3.5 h-3.5" />
                  <span>Jump to…</span>
                  <kbd className="ml-1 rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-500">⌘K</kbd>
                </button>
                <button type="button"
                        onClick={handleSignOut}
                        data-testid="platform-signout"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-white hover:bg-rose-50 hover:text-rose-700 border border-slate-300 text-[var(--ink-2)] text-xs font-semibold px-3 py-1.5 shadow-sm transition-colors">
                  <LogOut className="w-3.5 h-3.5" />
                  Sign out
                </button>
              </div>
            </div>
          </header>

          <main className="px-6 lg:px-8 py-6">
            <Outlet context={{ me }} />
          </main>
        </div>
      </div>

      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          items={ALL_ITEMS.filter((it) => it.cap == null || caps.has(it.cap))}
          go={(it) => { nav(it.to || ""); setPaletteOpen(false); }}
        />
      )}
    </div>
  );
}

function CommandPalette({ onClose, items, go }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) =>
      t.label.toLowerCase().includes(q) ||
      (t.section || "").toLowerCase().includes(q) ||
      (t.to || "").toLowerCase().includes(q)
    );
  }, [query, items]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  const onKey = useCallback((e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIdx]) go(results[activeIdx]);
    }
  }, [results, activeIdx, go]);

  return (
    <div data-testid="platform-cmdk-overlay"
         onClick={onClose}
         className="fixed inset-0 z-[9999] bg-slate-900/30 backdrop-blur-sm flex items-start justify-center pt-24 px-4">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Command className="w-4 h-4 text-[var(--primary)]" />
          <input
            ref={(el) => { if (el) el.focus(); }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to a page… (try: revenue, tenants, health)"
            data-testid="platform-cmdk-input"
            className="flex-1 bg-transparent border-0 outline-none text-sm text-[var(--ink)] placeholder:text-slate-400"
          />
          <kbd className="text-[10px] font-mono rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-slate-500">ESC</kbd>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-1.5">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No pages matched <b className="text-slate-700">{query}</b>.
            </div>
          ) : results.map((t, i) => (
            <button
              key={t.to}
              type="button"
              onClick={() => go(t)}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`platform-cmdk-result-${t.to || "dashboard"}`}
              className={
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors " +
                (i === activeIdx
                  ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                  : "hover:bg-slate-50 text-slate-700")
              }
            >
              <t.icon className="w-4 h-4 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{t.label}</div>
                <div className="text-[11px] text-slate-500">{t.section}</div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500 font-medium">
          <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {results.length} page{results.length === 1 ? "" : "s"}</span>
          <span className="hidden sm:inline">
            <kbd className="font-mono rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5">↑↓</kbd> nav
            <kbd className="font-mono rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 ml-2">↵</kbd> open
          </span>
        </div>
      </div>
    </div>
  );
}
